import { useEffect, useState } from "react";
import { format } from "date-fns";
import { CalendarDays } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { fmtDate, parseDmy } from "@/lib/format";
import { cn } from "@/lib/utils";

const toDate = (iso) => (iso ? new Date(`${iso}T00:00:00`) : undefined);

/** Date field shown as dd/mm/yyyy (typed or picked); value/onChange stay ISO yyyy-mm-dd. */
export const DateInput = ({ value, onChange, min, max, className, id, testid, clearable = false }) => {
  const shown = value ? fmtDate(value) : "";
  const [text, setText] = useState(shown);
  const [open, setOpen] = useState(false);
  useEffect(() => setText(shown), [shown]);
  const commit = (t) => {
    if (!t.trim() && clearable) return value && onChange("");
    const iso = parseDmy(t);
    if (iso && (!min || iso >= min) && (!max || iso <= max)) return iso !== value && onChange(iso);
    return setText(shown);
  };
  const disabled = [...(min ? [{ before: toDate(min) }] : []), ...(max ? [{ after: toDate(max) }] : [])];
  return (
    <div className={cn("relative h-9 w-40", className)}>
      <input id={id} value={text} placeholder="dd/mm/yyyy" inputMode="numeric" onChange={(e) => setText(e.target.value)}
        onBlur={(e) => commit(e.target.value)} onKeyDown={(e) => e.key === "Enter" && commit(e.currentTarget.value)}
        className="num h-full w-full rounded-md border border-input bg-white px-3 pr-8 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
        data-testid={testid} />
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button type="button" aria-label="Pick a date" className="absolute right-1.5 top-1/2 -translate-y-1/2 rounded p-1 text-slate-500 hover:bg-slate-100 hover:text-slate-900" data-testid={`${testid}-calendar`}>
            <CalendarDays className="h-4 w-4" />
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar mode="single" selected={toDate(value)} defaultMonth={toDate(value)} disabled={disabled}
            onSelect={(d) => { if (d) { onChange(format(d, "yyyy-MM-dd")); setOpen(false); } }} />
        </PopoverContent>
      </Popover>
    </div>
  );
};
